/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2022. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: AppConfig.ts
 * Created by: Kevin
 * Create Date: 2022-11-10 08:25
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

export default class AppConfig {

    // singleton
    private static _instance: AppConfig;

    public static get Instance() {
        return this._instance || (this._instance = new this())
    }

    private constructor() {
    }

    get isModeDev(): boolean {
        return process.env.VUE_APP_CURRENT_MODE === 'dev'
    }

    get isModeProd(): boolean {
        return process.env.VUE_APP_CURRENT_MODE === 'prod'
    }

    get isModeLocal(): boolean {
        return process.env.VUE_APP_CURRENT_MODE === 'local'
    }

    get apiDefaultTimeout(): number {
        const value = process.env.VUE_APP_API_TIMEOUT || '10000'
        const v = parseInt(value)
        if (isNaN(v)) {
            return 10000;
        }
        return v;
    }

    get basicAuthUser(): string {
        return process.env.VUE_APP_BASIC_AUTH_USER || ""
    }

    get basicAuthPassword(): string {
        return process.env.VUE_APP_BASIC_AUTH_SECURE || ""
    }

    /**
     * Cookie domain for clearing profile cookies — always the team-site service
     * domain, derived from VUE_APP_TEAM_SERVICE_DOMAIN.
     */
    get appDomainName(): string {
        return AppConfig.Instance.apiServiceDomain
    }

    get apiURLServiceTeam(): string {
        return process.env.VUE_APP_API_URL_SERVICE_TEAM || "/teamsite-service/ztn"
    }

    /**
     * Auth-site base URL (logged-out redirect fallback). Production has a separate
     * auth site; every other environment derives it from VUE_APP_TEAM_SERVICE_*.
     */
    get authSiteURL(): string {
        if (AppConfig.Instance.isModeProd) {
            return "https://auth.stronggate.com:38889";
        }
        const scheme = AppConfig.Instance.apiServiceScheme;
        const port = AppConfig.Instance.apiServicePort;
        const isDefaultPort = (scheme === "https" && port === "443") || (scheme === "http" && port === "80");
        const portSuffix = isDefaultPort ? "" : `:${port}`;
        return `${scheme}://${AppConfig.Instance.apiServiceDomain}${portSuffix}`;
    }

    get apiServiceDomain(): string {
        return process.env.VUE_APP_TEAM_SERVICE_DOMAIN || "ts.stronggate.com"
    }

    get apiServicePort(): string {
        return process.env.VUE_APP_TEAM_SERVICE_PORT || "28388"
    }

    /**
     * Transport scheme for the team-site service. Defaults to https; only an
     * explicit "http" downgrades (typos keep the safe default) — local dev runs
     * the service with an empty server_tls, i.e. plain HTTP.
     */
    get apiServiceScheme(): string {
        return process.env.VUE_APP_TEAM_SERVICE_SCHEME === "http" ? "http" : "https"
    }

    /**
     * True when the page was opened on the bare service domain instead of a team subdomain.
     *
     * The app is addressed per team: every API call goes to <team>.<serviceDomain>. On the bare
     * host there is no team to take, and the derivation below would happily build
     * "ts-dev.ts-dev.ztn-dev.sogate.net" from it — a host that resolves (the domain is wildcard)
     * but serves nothing, so the app reported "cannot reach the service" on what is really a
     * wrong address. Callers send the visitor to the sign-in page instead of guessing a team.
     */
    get isBareServiceHost(): boolean {
        const firstLabel = window.location.hostname.split(':')[0].split('.')[0];
        return firstLabel === AppConfig.Instance.apiServiceDomain.split('.')[0];
    }

    /** The server-rendered signup page. */
    get signupURL(): string {
        return `${AppConfig.Instance.authSiteURL}${AppConfig.Instance.apiURLServiceTeam}/auth/to/signup`;
    }

    /**
     * The server-rendered sign-in page (it asks for a team domain, and links to signup). Given a
     * team, it opens with the Team Domain field filled in: index.gohtml reads `?team=`.
     */
    signinURL(team?: string): string {
        const page = `${AppConfig.Instance.authSiteURL}${AppConfig.Instance.apiURLServiceTeam}`;
        return team ? `${page}?team=${encodeURIComponent(team)}` : page;
    }

    /** The team the console was opened for: the first label of the host, "kwstg01" in kwstg01.ts.… */
    get teamSlug(): string {
        return window.location.hostname.split(':')[0].split('.')[0];
    }

    get apiServiceTeam(): string {
        return `${AppConfig.Instance.apiServiceScheme}://${AppConfig.Instance.teamSlug}.${AppConfig.Instance.apiServiceDomain}:${AppConfig.Instance.apiServicePort}`;
    }

    /*get loginURL(): string {
        return process.env.VUE_APP_LOGIN_URL || "https://auth.stronggate.com:38889"
    }*/
}